import { z } from "@hono/zod-openapi";
import { RRule } from "rrule";
import {
  EventSchema,
  EventSessionSchema,
  EventPricePointSchema,
  TagSchema,
  EventBoatSchema,
  EventParticipantSchema,
} from "../generated/zod/schemas/models";
import { deriveFieldTypes } from "../utils/filters";
import { validatePricePointCoverage } from "../utils/events/price-points";
import { paginationSchema, paginationMetaSchema } from "../utils/pagination";

const MAX_RRULE_OCCURRENCES = 30;

const exceedsMaxOccurrences = (rrule: string, dtstart: Date) => {
  try {
    const opts = RRule.parseString(rrule);
    const rule = new RRule({ ...opts, dtstart });
    // Compute at most MAX+1 occurrences so we don't hang on unbounded rules
    const occurrences = rule.all((_date, i) => i < MAX_RRULE_OCCURRENCES + 1);
    return occurrences.length > MAX_RRULE_OCCURRENCES;
  } catch {
    return false; // Let Zod validation handle invalid RRules
  }
}

export const eventSessionInputSchema =
  EventSessionSchema.pick({
    id: true,
    startsAt: true,
    endsAt: true,
    label: true,
  })
  .partial({ id: true })
  .superRefine((data, ctx: z.RefinementCtx) => {
    if (data.endsAt.getTime() <= data.startsAt.getTime()) {
      ctx.addIssue({
        code: "custom",
        message: "Session end must be after start",
        path: ["endsAt"],
      });
    }
  });

export const pricePointAvailabilityEnumSchema = z.enum([
  "unlimited",
  "available",
  "nearly_full",
  "full",
]);

export const eventPricePointInputSchema =
  EventPricePointSchema.pick({
    id: true,
    availableFrom: true,
    availableUntil: true,
    depositCents: true,
    balanceDueAt: true,
    priceCents: true,
    currency: true,
    sortOrder: true,
    label: true,
    description: true,
    capacity: true,
  })
  .partial({ id: true })
  .superRefine((data, ctx: z.RefinementCtx) => {
    if (data.depositCents) {
      if (data.depositCents > data.priceCents - 500) {
        ctx.addIssue({
          code: "custom",
          message:
            "Deposit must be at least 500 pence (5 GBP) less than total price",
          path: ["depositCents"],
        });
      }

      if (!data.balanceDueAt) {
        ctx.addIssue({
          code: "custom",
          message: "Balance due date is required if a deposit is set",
          path: ["balanceDueAt"],
        });
      }
    } else if (data.balanceDueAt) {
      ctx.addIssue({
        code: "custom",
        message: "Balance due date must not be set if no deposit is set",
        path: ["balanceDueAt"],
      });
    }
  });

const recurrenceInputSchema = EventSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true
}).extend({
  rrule: z.string().optional().nullable(),
  imagePool: z.array(z.string().min(1)).optional().default([]),
  isRecurring: z.boolean().optional().default(false),
  // TOOD Confirm Tags actually Work
  tags: z.array(z.string().min(1)).optional().default([]),
});

export const bookableEventCreateSchemaBase = recurrenceInputSchema.omit({
  startsAt: true,
  endsAt: true,
}).extend({
  isBookable: z.literal(true),
  sessions: z
    .array(eventSessionInputSchema)
    .min(1, "At least one session is required"),
  pricePoints: z
    .array(eventPricePointInputSchema)
    .min(1, "At least one price point is required"),
});

// Non-bookable event creation - minimal fields, explicit startsAt/endsAt, and must not include booking fields
// Derive from model schema - dates are already coerced by default
export const nonBookableEventCreateSchemaBase = recurrenceInputSchema.omit({
  audience: true,
  bookingClosesAt: true,
  requiresConsentForm: true,
  requiresPhotoConsent: true,
  schedule: true,
  whatToBring: true,
  imageUrl: true,
  imagePool: true,
})
  .extend({
    isBookable: z.literal(false),
  })

const createEventSchemaBase = z.discriminatedUnion("isBookable", [
  bookableEventCreateSchemaBase,
  nonBookableEventCreateSchemaBase,
]);

export const createEventSchema = createEventSchemaBase.superRefine((data, ctx) => {
  if (data.isRecurring) {
    if (!data.rrule || data.rrule.trim() === "") {
      ctx.addIssue({
        code: "custom",
        message: "Recurrence rule is required for recurring events",
        path: ["rrule"],
      });
    } else {
      try {
        const anchor =
          data.isBookable
            ? data.sessions.reduce<Date | null>((earliest, s) => {
                if (!earliest || s.startsAt < earliest) return s.startsAt;
                return earliest;
              }, null)
            : data.startsAt;

        if (anchor && exceedsMaxOccurrences(data.rrule, anchor)) {
          ctx.addIssue({
            code: "custom",
            message: `Recurrence cannot generate more than ${MAX_RRULE_OCCURRENCES} occurrences`,
            path: ["rrule"],
          });
        }
      } catch {
        ctx.addIssue({
          code: "custom",
          message: "Invalid recurrence rule",
          path: ["rrule"],
        });
      }
    }
  } else {
    if (data.rrule && data.rrule.trim() !== "") {
      ctx.addIssue({
        code: "custom",
        message: "Recurrence rule must be empty for non-recurring events",
        path: ["rrule"],
      });
    }
    if (data.isBookable && data.imagePool && data.imagePool.length > 0) {
      ctx.addIssue({
        code: "custom",
        message: "Image pool must be empty for non-recurring events",
        path: ["imagePool"],
      });
    }
  }

  if (data.isBookable) {
    // Calculate startsAt from sessions - find the earliest session start time
    const startsAt = data.sessions.reduce((earliest: Date | null, s) => 
      !earliest || s.startsAt < earliest ? s.startsAt : earliest
    , null);

    if (!startsAt) {
      ctx.addIssue({
        code: "custom",
        message: "At least one session is required",
        path: ["sessions"],
      });
      return; // Skip further validation if no valid start time
    }

    // Validate price point coverage for published events
    const validation = validatePricePointCoverage(
      data.pricePoints.map((pp) => ({
        availableFrom: pp.availableFrom,
        availableUntil: pp.availableUntil,
      })),
      data.bookingClosesAt ?? null,
      startsAt,
      data.isPublished ?? false,
      data.timezone,
    );

    if (!validation.valid) {
      ctx.addIssue({
        code: "custom",
        message: validation.error || "Invalid price point coverage",
        path: ["pricePoints"],
      });
      // Also attach to bookingClosesAt so users see the error on the field
      ctx.addIssue({
        code: "custom",
        message: validation.error || "Invalid price point coverage",
        path: ["bookingClosesAt"],
      });
    }

    // Validate bookingClosesAt is within 5 hours after startsAt if provided
    if (data.bookingClosesAt) {
      const fiveHoursAfterStart = new Date(startsAt.getTime() + 5 * 60 * 60 * 1000);
      if (data.bookingClosesAt > fiveHoursAfterStart) {
        ctx.addIssue({
          code: "custom",
          message: "Booking closes date must be no more than 5 hours after event start date",
          path: ["bookingClosesAt"],
        });
      }
    }

    // Validate price point balance due dates are before or on event start
    data.pricePoints.forEach((pp, index) => {
      if (pp.balanceDueAt && pp.balanceDueAt > startsAt) {
        ctx.addIssue({
          code: "custom",
          message: "Balance due date must be on or before event start date",
          path: ["pricePoints", index, "balanceDueAt"],
        });
      }
    });
  } else {
    // Non-bookable checks
    if (data.endsAt.getTime() <= data.startsAt.getTime()) {
      ctx.addIssue({
        code: "custom",
        message: "Event end must be after start",
        path: ["endsAt"],
      });
    }
  }
});

const tagEnvelopeSchema = z.object({
  tag: TagSchema,
});

export const eventResponseSchema = EventSchema.extend({
  sessions: z.array(EventSessionSchema),
  pricePoints: z.array(EventPricePointSchema),
  tags: z.array(tagEnvelopeSchema),
  boats: z.array(EventBoatSchema).optional(),
});

// EventBoat list (for race events); eventId comes from URL path
export const boatCrewMemberSchema = EventParticipantSchema.pick({
  id: true,
  firstName: true,
  lastName: true,
  status: true,
  group: true,
  photoConsent: true,
  consentFormParticipantDateOfBirth: true,
  consentFormImportantMedicalInformation: true,
  consentFormMedicationDetails: true,
  consentFormInjuriesDetails: true,
  consentFormDietaryRequirementsDetails: true,
  consentFormDisabilityDetails: true,
});

export const boatDecisionSchema = z.object({
  reason: z.string().min(1),
});

export const boatCancelSchema = boatDecisionSchema.extend({
  refund: z.boolean().optional().default(false),
});

export const boatResponseSchema = EventBoatSchema.extend({
  crew: z.array(boatCrewMemberSchema),
  pricePoint: EventPricePointSchema.pick({ id: true, label: true }).nullable().optional(),
});

export const publicEventResponseSchema = EventSchema.omit({
  createdAt: true,
  updatedAt: true,
}).extend({
  sessions: z.array(EventSessionSchema.pick({ startsAt: true, endsAt: true, label: true })),
  pricePoints: z.array(
    EventPricePointSchema.pick({
      id: true,
      label: true,
      description: true,
      priceCents: true,
      currency: true,
      sortOrder: true,
      availableFrom: true,
      availableUntil: true,
      depositCents: true,
      balanceDueAt: true,
      capacity: true,
    }).extend({
      availability: pricePointAvailabilityEnumSchema,
    }),
  ),
  tags: z
    .array(z.object({ tag: TagSchema }))
    .transform((tags) => tags.map(({ tag }) => tag.name))
    .openapi({
      type: "array",
      items: { type: "string" },
      description: "Array of tag names",
    }),
});

export type CreateEventInput = z.infer<typeof createEventSchema>;

export const eventEnvelopeSchema = z.object({
  event: eventResponseSchema,
});

export const publicEventEnvelopeSchema = z.object({
  event: publicEventResponseSchema,
});

// Event list query schema with filtering
export const createEventListQuerySchema = () => {
  const eventFilterFields = deriveFieldTypes(eventResponseSchema);
  return z.object({
    ...paginationSchema.shape,
    // Add all event fields as optional filter parameters
    ...Object.fromEntries(
      Object.keys(eventFilterFields).map((field) => [
        field,
        z.string().optional(),
      ]),
    ),
    // Add date range filtering
    dateFrom: z.string().datetime().optional(), // ISO 8601 datetime string
    dateTo: z.string().datetime().optional(), // ISO 8601 datetime string
    tagIds: z.string().optional(), // Comma-separated tag IDs
    search: z.string().optional(), // Search in title and description
  });
};

// Paginated event list response
export const paginatedEventListResponseSchema = z.object({
  data: z.array(eventResponseSchema),
  pagination: paginationMetaSchema,
});

export const publicPaginatedEventListResponseSchema = z.object({
  data: z.array(publicEventResponseSchema),
  pagination: paginationMetaSchema,
});

export const eventParticipantStatsRowSchema = z.object({
  eventPricePointId: z.string().nullable(),
  label: z.string(),
  count: z.number().int().nonnegative(),
});

export const eventParticipantStatsGroupRowSchema = z.object({
  group: z.string().nullable(),
  label: z.string(),
  count: z.number().int().nonnegative(),
});

export const eventParticipantStatsResponseSchema = z.object({
  total: z.number().int().nonnegative(),
  byPricePoint: z.array(eventParticipantStatsRowSchema),
  byGroup: z.array(eventParticipantStatsGroupRowSchema).optional(),
});

export const eventParticipantGroupsResponseSchema = z.object({
  groups: z.array(z.string()),
});
